import { useCallback, useEffect, useState } from 'react'
import Editor from 'react-simple-code-editor'
import { highlight, languages } from 'prismjs'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'

import { usePrompt } from '@hooks'

import 'prismjs/components/prism-json'

import 'prismjs/themes/prism.css'

interface JsonEditorProps {
  value: string
  onChange: (value: string) => void
  label?: string
  readOnly?: boolean
  minHeight?: number
  maxHeight?: number
  hideActions?: boolean
  onValidationChange?: (isValid: boolean, error: string | null) => void
}

export const JsonEditor = ({
  value,
  onChange,
  label = 'JSON Editor',
  readOnly = false,
  minHeight = 200,
  maxHeight = 600,
  hideActions = false,
  onValidationChange,
}: JsonEditorProps) => {
  const [localValue, setLocalValue] = useState(value)
  const [error, setError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  usePrompt({ when: hasChanges && !hideActions })

  useEffect(() => {
    setLocalValue(value)
    setHasChanges(false)
  }, [value])

  const handleChange = useCallback(
    (code: string) => {
      setLocalValue(code)
      const hasChange = code !== value
      setHasChanges(hasChange)
      
      let validationError: string | null = null
      if (code.trim()) {
        try {
          JSON.parse(code)
        } catch (e) {
          validationError = (e as Error).message
        }
      }
      
      setError(validationError)
      onValidationChange?.(validationError === null, validationError)
      
      // If hideActions is true, call onChange immediately
      if (hideActions) {
        onChange(code)
      }
    },
    [value, hideActions, onChange, onValidationChange]
  )

  const handleApply = useCallback(() => {
    try {
      if (localValue.trim()) {
        JSON.parse(localValue)
      }
      onChange(localValue)
      setHasChanges(false)
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [localValue, onChange])

  const handleReset = useCallback(() => {
    setLocalValue(value)
    setHasChanges(false)
    setError(null)
  }, [value])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant='subtitle2'>{label}</Typography>
        {!readOnly && !hideActions && (
          <Stack direction='row' spacing={1}>
            {hasChanges && (
              <>
                <Button size='small' variant='outlined' onClick={handleReset}>
                  Reset
                </Button>
                <Button size='small' variant='contained' disabled={!!error} onClick={handleApply}>
                  Apply Changes
                </Button>
              </>
            )}
          </Stack>
        )}
      </Box>
      {error && (
        <Alert severity='error' sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {!hideActions && hasChanges && !error && (
        <Alert severity='info' sx={{ mb: 1 }}>
          You have unsaved changes. Click "Apply Changes" to save them.
        </Alert>
      )}
      <Box
        sx={{
          border: '1px solid',
          borderColor: error ? 'error.main' : hasChanges ? 'warning.main' : 'divider',
          borderRadius: 1,
          overflow: 'auto',
          minHeight,
          maxHeight,
          backgroundColor: readOnly ? 'action.disabledBackground' : 'background.paper',
          '& textarea': {
            outline: 'none !important',
            pointerEvents: readOnly ? 'none' : 'auto',
          },
          '& pre': {
            margin: 0,
            padding: 0,
          },
          '& .token.property': {
            color: '#905',
          },
          '& .token.string': {
            color: '#690',
          },
          '& .token.number': {
            color: '#905',
          },
          '& .token.boolean': {
            color: '#905',
          },
          '& .token.null': {
            color: '#905',
          },
        }}
      >
        <Editor
          value={localValue}
          onValueChange={handleChange}
          highlight={code => highlight(code, languages.json, 'json')}
          padding={10}
          disabled={readOnly}
          style={{
            fontFamily: '"Fira code", "Fira Mono", monospace',
            fontSize: 14,
            minHeight: minHeight - 2,
            pointerEvents: readOnly ? 'none' : 'auto',
          }}
        />
      </Box>
    </Box>
  )
}
